import {alpha, styled} from '@mui/material/styles';
import {TextField, TextFieldProps, Typography} from '@mui/material';
import {isNil} from 'lodash/fp';
import {useMemo} from 'react';

import {dayjs, formatDateToString, yearFromNow, todayString} from 'utils/dateUtils';

import {DesktopDatePicker} from 'components/DatePicker/DesktopDatePicker';

const SContainer = styled('div')(({theme}) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 155px)',
  gap: theme.spacing(2),
  alignItems: 'start',
}));

const SWrapper = styled('div')(({theme}) => ({
  display: 'grid',
  gap: theme.spacing(2),
}));

export const STextField = styled(TextField)(({theme}) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadiusRegular,
    ...theme.typography.buttonSmall,

    '& .MuiOutlinedInput-input': {
      height: 44,
      boxSizing: 'border-box',
      padding: theme.spacing(3.5, 4),
      paddingRight: 0,
      textTransform: 'lowercase',
    },

    '& fieldset': {
      borderColor: alpha(theme.palette.primary.main, 0.12),
    },

    '& .MuiButtonBase-root': {
      marginRight: theme.spacing(-1),
    },
  },
}));

const TextFieldSlot = (label: string, testId: string) => (props: TextFieldProps) => {
  return (
    <SWrapper>
      <Typography variant='body3' color='text.secondary'>
        {label}
      </Typography>
      <STextField data-testid={testId} size='small' variant='outlined' {...props} />
    </SWrapper>
  );
};

const FromTextFieldSlot = TextFieldSlot('From', 'from-input');
const ToTextFieldSlot = TextFieldSlot('To', 'to-input');

// FIXME: Remove internal validation
export const FromToDatePicker = ({
  maxDate = yearFromNow,
  className,
  from,
  to,
  onChange,
  onBlur,
  dataTestId,
  showValidation = true,
}: {
  // Set to todayString to disable future dates; set to null to allow all dates
  maxDate?: string | null;
  className?: string;
  from?: number | string | null;
  to?: number | string | null;
  onChange: (from: string | null, to: string | null) => void;
  onBlur?: () => void;
  showValidation?: boolean;
} & DataTestId) => {
  const fromValue = !isNil(from) ? formatDateToString(from) : null;
  const toValue = !isNil(to) ? formatDateToString(to) : null;

  const disableFuture = useMemo(() => maxDate === todayString, [maxDate]);

  return (
    <SContainer className={className} data-testid={dataTestId}>
      <DesktopDatePicker
        slots={{
          textField: FromTextFieldSlot,
        }}
        value={fromValue}
        maxDate={maxDate}
        maxValidDate={toValue && dayjs(toValue).isBefore(maxDate) ? toValue : maxDate}
        onChange={value => {
          onChange(value, toValue);
        }}
        onBlur={onBlur}
        showValidation={showValidation}
        disableFuture={disableFuture}
      />

      <DesktopDatePicker
        slots={{
          textField: ToTextFieldSlot,
        }}
        minDate={formatDateToString(dayjs('1990-01-01'))}
        maxDate={maxDate}
        minValidDate={fromValue}
        maxValidDate={maxDate}
        value={toValue}
        onChange={value => {
          onChange(fromValue, value);
        }}
        onBlur={onBlur}
        withTodayButton
        showValidation={showValidation}
        disableFuture={disableFuture}
      />
    </SContainer>
  );
};
